export function PrivacyPolicyContent() {
  return (
    <div className="prose prose-sm max-w-none text-foreground">
      <h2 className="text-lg font-semibold">Privacy Policy</h2>
      <p className="text-sm text-muted-foreground">
        Last updated: February 23, 2026
      </p>

      <h3 className="mt-6 text-base font-semibold">1. Introduction</h3>
      <p className="text-sm leading-relaxed">
        C2 Vault, LLC (&ldquo;C2 Vault,&rdquo; &ldquo;Company,&rdquo;
        &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) is committed
        to protecting the privacy and security of your information. This Privacy
        Policy describes how we collect, use, disclose, and safeguard
        information when you access or use our website, software platform, and
        related services (collectively, the &ldquo;Application&rdquo;).
      </p>
      <p className="text-sm leading-relaxed">
        By accessing or using the Application, you acknowledge that you have
        read, understood, and agree to be bound by this Privacy Policy. If you
        do not agree with this Privacy Policy, please do not access or use the
        Application. This Privacy Policy should be read together with our End
        User License Agreement.
      </p>

      <h3 className="mt-6 text-base font-semibold">
        2. Information We Collect
      </h3>
      <p className="text-sm leading-relaxed">
        We collect the following categories of information:
      </p>

      <p className="text-sm leading-relaxed font-medium mt-3">
        (a) Information You Provide Directly
      </p>
      <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1">
        <li>
          <span className="font-medium">Pharmacy Registration Details:</span>{" "}
          Pharmacy name, legal name, National Provider Identifier (NPI), DEA
          registration number, state license number, address, phone number, and
          contact information.
        </li>
        <li>
          <span className="font-medium">User Account Information:</span> Name,
          initials, email address, role, and login credentials.
        </li>
        <li>
          <span className="font-medium">
            Controlled Substance Transaction Records:
          </span>{" "}
          Prescription reference numbers, drug names, NDC codes, quantities,
          dates, and chain-of-custody information you enter into the system.
        </li>
        <li>
          <span className="font-medium">Support Communications:</span>{" "}
          Information you provide when contacting our support team, including
          the content of messages, attachments, and related metadata.
        </li>
        <li>
          <span className="font-medium">Payment Information:</span> Billing
          details processed through our third-party payment processor. We do not
          store full credit card numbers on our servers.
        </li>
      </ul>

      <p className="text-sm leading-relaxed font-medium mt-3">
        (b) Information Collected Automatically
      </p>
      <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1">
        <li>
          <span className="font-medium">Usage Data:</span> Pages visited,
          features used, actions taken, timestamps, and frequency of use.
        </li>
        <li>
          <span className="font-medium">Device and Browser Information:</span>{" "}
          IP address, browser type and version, operating system, device type,
          screen resolution, and language preferences.
        </li>
        <li>
          <span className="font-medium">Audit Log Data:</span> Automatically
          generated records of user activity within the Application for security
          and compliance purposes.
        </li>
        <li>
          <span className="font-medium">Cookies and Similar Technologies:</span>{" "}
          Essential cookies for authentication and session management. See
          Section 9 for details.
        </li>
      </ul>

      <p className="text-sm leading-relaxed font-medium mt-3">
        (c) Information from Third Parties
      </p>
      <p className="text-sm leading-relaxed">
        We may receive information from third-party services integrated with the
        Application, such as payment processors (e.g., Stripe) for subscription
        management, and NPI registry data for pharmacy verification during
        registration.
      </p>

      <h3 className="mt-6 text-base font-semibold">
        3. How We Use Your Information
      </h3>
      <p className="text-sm leading-relaxed">
        We use the information we collect for the following purposes:
      </p>
      <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1">
        <li>
          <span className="font-medium">Service Delivery:</span> To provide,
          operate, maintain, and improve the Application, including processing
          controlled substance records, generating compliance reports, and
          maintaining audit trails.
        </li>
        <li>
          <span className="font-medium">Account Management:</span> To create and
          manage your account, authenticate users, and process subscription
          payments.
        </li>
        <li>
          <span className="font-medium">Communications:</span> To send technical
          notices, security alerts, updates, support responses, and
          administrative messages related to the Application.
        </li>
        <li>
          <span className="font-medium">Security and Fraud Prevention:</span> To
          detect, prevent, and address technical issues, security threats,
          fraud, and unauthorized access.
        </li>
        <li>
          <span className="font-medium">Analytics and Improvement:</span> To
          analyze usage patterns, diagnose issues, and improve the
          functionality, performance, and user experience of the Application.
        </li>
        <li>
          <span className="font-medium">Legal Compliance:</span> To comply with
          applicable laws, regulations, legal processes, or governmental
          requests.
        </li>
      </ul>

      <h3 className="mt-6 text-base font-semibold">
        4. Data Sharing and Disclosure
      </h3>
      <p className="text-sm leading-relaxed">
        We do not sell, rent, or trade your personal information. We may share
        your information only in the following circumstances:
      </p>
      <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1">
        <li>
          <span className="font-medium">Service Providers:</span> We share
          information with trusted third-party vendors who assist in operating
          the Application, including cloud hosting providers, payment
          processors, email delivery services, and analytics providers. These
          providers are contractually obligated to protect your information and
          use it only for the services they provide to us.
        </li>
        <li>
          <span className="font-medium">Legal Requirements:</span> We may
          disclose your information if required by law, subpoena, court order,
          or governmental regulation, or when we believe disclosure is necessary
          to protect our rights, your safety, or the safety of others.
        </li>
        <li>
          <span className="font-medium">Business Transfers:</span> In the event
          of a merger, acquisition, reorganization, bankruptcy, or sale of all
          or a portion of our assets, your information may be transferred as
          part of that transaction. We will notify you of any such change and
          any choices you may have regarding your information.
        </li>
        <li>
          <span className="font-medium">Professional Advisors:</span> We may
          share information with our legal, accounting, and financial advisors
          under confidentiality obligations as necessary for legitimate business
          purposes.
        </li>
        <li>
          <span className="font-medium">With Your Consent:</span> We may share
          information for any other purpose with your explicit consent.
        </li>
      </ul>

      <h3 className="mt-6 text-base font-semibold">5. Data Security</h3>
      <p className="text-sm leading-relaxed">
        We implement industry-standard security measures to protect your data,
        including:
      </p>
      <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1">
        <li>
          <span className="font-medium">Encryption in Transit:</span> All data
          transmitted between your browser and our servers is encrypted using
          Transport Layer Security (TLS).
        </li>
        <li>
          <span className="font-medium">Encryption at Rest:</span> Stored data
          is encrypted using AES-256 encryption.
        </li>
        <li>
          <span className="font-medium">Access Controls:</span> Role-based
          access controls and authentication mechanisms to restrict access to
          authorized personnel only.
        </li>
        <li>
          <span className="font-medium">Audit Logging:</span> Comprehensive
          logging of system access and data modifications for security
          monitoring.
        </li>
        <li>
          <span className="font-medium">Regular Assessments:</span> Periodic
          security reviews and vulnerability assessments.
        </li>
      </ul>
      <p className="text-sm leading-relaxed">
        However, no method of electronic transmission or storage is 100% secure.
        While we strive to use commercially reasonable means to protect your
        information, we cannot guarantee its absolute security. In the event of
        a data breach that affects your personal information, we will notify you
        in accordance with applicable law.
      </p>

      <h3 className="mt-6 text-base font-semibold">6. Data Retention</h3>
      <p className="text-sm leading-relaxed">
        We retain your data for as long as your account is active or as needed
        to provide the Application. Specific retention periods include:
      </p>
      <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1">
        <li>
          <span className="font-medium">Account Data:</span> Retained for the
          duration of your active subscription and for a reasonable period
          thereafter to facilitate account reactivation or data export.
        </li>
        <li>
          <span className="font-medium">Controlled Substance Records:</span>{" "}
          Retained in accordance with DEA regulations and applicable state
          regulatory requirements, which generally require a minimum retention
          period of two (2) years.
        </li>
        <li>
          <span className="font-medium">Audit Logs:</span> Retained for a
          minimum of two (2) years for compliance purposes.
        </li>
        <li>
          <span className="font-medium">Post-Termination:</span> Upon account
          termination, you will have thirty (30) days to download your data.
          After this period, Company may permanently delete all account data.
        </li>
      </ul>
      <p className="text-sm leading-relaxed">
        You are solely responsible for maintaining your own backups or copies of
        records as required by federal, state, or regulatory law.
      </p>

      <h3 className="mt-6 text-base font-semibold">
        7. Your Rights and Choices
      </h3>
      <p className="text-sm leading-relaxed">
        Subject to applicable law and regulatory retention requirements, you
        have the following rights regarding your personal information:
      </p>
      <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1">
        <li>
          <span className="font-medium">Access:</span> You may request access to
          the personal information we hold about you.
        </li>
        <li>
          <span className="font-medium">Correction:</span> You may request that
          we correct inaccurate or incomplete personal information.
        </li>
        <li>
          <span className="font-medium">Deletion:</span> You may request
          deletion of your personal information, subject to legal retention
          requirements (e.g., DEA recordkeeping obligations).
        </li>
        <li>
          <span className="font-medium">Data Portability:</span> You may export
          your data at any time through the Application.
        </li>
        <li>
          <span className="font-medium">Opt-Out:</span> You may opt out of
          non-essential communications at any time by following the unsubscribe
          instructions in our emails or contacting us directly.
        </li>
      </ul>
      <p className="text-sm leading-relaxed">
        To exercise any of these rights, please contact us at{" "}
        <a href="mailto:support@c2vault.com" className="text-primary underline">
          support@c2vault.com
        </a>
        . We will respond to your request within thirty (30) days.
      </p>

      <h3 className="mt-6 text-base font-semibold">
        8. HIPAA and Protected Health Information
      </h3>
      <p className="text-sm leading-relaxed">
        The Application is not intended to store or process patient names, dates
        of birth, addresses, diagnoses, or other protected health information
        (&ldquo;PHI&rdquo;) as defined under the Health Insurance Portability
        and Accountability Act (&ldquo;HIPAA&rdquo;). Users agree not to enter
        PHI into the Application except to the limited extent necessary for
        prescription tracking using non-identifiable prescription reference
        numbers.
      </p>
      <p className="text-sm leading-relaxed">
        To the extent we process PHI on your behalf, we do so in accordance with
        the terms of a separately executed Business Associate Agreement
        (&ldquo;BAA&rdquo;) and applicable HIPAA regulations. Company does not
        assume the role of a Business Associate unless expressly agreed to in a
        separate written agreement signed by both parties. You remain solely
        responsible for compliance with HIPAA and all other applicable privacy
        laws.
      </p>

      <h3 className="mt-6 text-base font-semibold">
        9. Cookies and Tracking Technologies
      </h3>
      <p className="text-sm leading-relaxed">
        We use the following types of cookies and similar technologies:
      </p>
      <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1">
        <li>
          <span className="font-medium">Essential Cookies:</span> Required for
          authentication, session management, and security. These cookies are
          necessary for the Application to function and cannot be disabled.
        </li>
        <li>
          <span className="font-medium">Analytics Cookies:</span> We use
          analytics tools (such as Google Analytics) to understand how users
          interact with the Application and to improve performance and user
          experience. These tools may collect anonymized usage data.
        </li>
      </ul>
      <p className="text-sm leading-relaxed">
        We do not use advertising cookies, behavioral tracking, or retargeting
        technologies. We do not sell data collected through cookies to third
        parties.
      </p>

      <h3 className="mt-6 text-base font-semibold">
        10. Children&apos;s Privacy
      </h3>
      <p className="text-sm leading-relaxed">
        The Application is intended for use by licensed pharmacies and
        authorized pharmacy personnel who are at least 18 years of age. We do
        not knowingly collect personal information from individuals under 18. If
        we become aware that we have collected information from an individual
        under 18, we will take steps to delete such information promptly.
      </p>

      <h3 className="mt-6 text-base font-semibold">
        11. International Data Transfers
      </h3>
      <p className="text-sm leading-relaxed">
        The Application is hosted and operated in the United States. If you
        access the Application from outside the United States, please be aware
        that your information may be transferred to, stored, and processed in
        the United States, where data protection laws may differ from those in
        your jurisdiction. By using the Application, you consent to the transfer
        of your information to the United States.
      </p>

      <h3 className="mt-6 text-base font-semibold">
        12. Third-Party Links and Services
      </h3>
      <p className="text-sm leading-relaxed">
        The Application may contain links to third-party websites or services
        that are not owned or controlled by C2 Vault. We are not responsible for
        the privacy practices, content, or security of any third-party websites
        or services. We encourage you to review the privacy policies of any
        third-party services you access through or in connection with the
        Application.
      </p>

      <h3 className="mt-6 text-base font-semibold">
        13. Changes to This Privacy Policy
      </h3>
      <p className="text-sm leading-relaxed">
        We may update this Privacy Policy from time to time to reflect changes
        in our practices, technology, legal requirements, or other factors. If
        we make material changes, we will notify you by email or through a
        notice within the Application prior to the changes taking effect. Your
        continued use of the Application after such changes constitutes your
        acceptance of the updated Privacy Policy. We encourage you to review
        this Privacy Policy periodically.
      </p>

      <h3 className="mt-6 text-base font-semibold">14. Governing Law</h3>
      <p className="text-sm leading-relaxed">
        This Privacy Policy shall be governed by and construed in accordance
        with the laws of the State of New York, without regard to its conflict
        of law principles. Any disputes arising under or in connection with this
        Privacy Policy are subject to the dispute resolution provisions set
        forth in our Terms of Use.
      </p>

      <h3 className="mt-6 text-base font-semibold">15. Contact Information</h3>
      <p className="text-sm leading-relaxed">
        If you have questions, concerns, or requests regarding this Privacy
        Policy or our data practices, please contact us:
      </p>
      <p className="text-sm leading-relaxed mt-2">C2 Vault, LLC</p>
      <p className="text-sm leading-relaxed">
        Email:{" "}
        <a href="mailto:support@c2vault.com" className="text-primary underline">
          support@c2vault.com
        </a>
      </p>
    </div>
  );
}
